import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

// Define a fallback implementation to handle different OneSignal plugin versions
export class OneSignalAdapter {
  private plugin: any;
  private legacyMode: boolean = false;
  private modernSDK5: boolean = false;
  private isInitialized: boolean = false; // Track if OneSignal is initialized with app ID
  
  constructor() {
    // For Cordova plugins, we need to access them differently
    if (window.cordova && window.plugins) {
      this.plugin = window.plugins?.OneSignal;
      
    } else {
      // Capacitor plugins are typically accessed differently
      this.plugin = window['plugins']?.OneSignal;
      
    }
    
    // Detect which version/API style we're using
    this.detectApiVersion();
  }
  
  private detectApiVersion(): void {
    if (!this.isAvailable()) {
      
      return;
    }
    
    // Check for SDK 5.x structure (has User and Notifications namespaces)
    if (this.plugin.User && this.plugin.Notifications) {
      this.modernSDK5 = true;
      this.legacyMode = false;
      
    }
    // Check for legacy Cordova style: uses init method instead of setAppId
    else if (
      (typeof this.plugin.init === 'function' && typeof this.plugin.setAppId !== 'function') ||
      (window.cordova && window.cordova.exec)
    ) {
      this.legacyMode = true;
      this.modernSDK5 = false;
      
    }
    // Default to modern SDK 4.x
    else {
      this.legacyMode = false;
      this.modernSDK5 = false;
      
    }
  }
  
  isAvailable(): boolean {
    return !!this.plugin;
  }

  isInitializedWithAppId(): boolean {
    return this.isInitialized;
  }

  getPlugin(): any {
    return this.plugin;
  }
  
  async setAppId(appId: string): Promise<void> {
    if (!this.isAvailable()) return;
    
    try {
      // Special handling for Cordova OneSignal plugin with legacy mode
      if (this.legacyMode && window.cordova && window.cordova.exec) {
        
        return new Promise((resolve, reject) => {
          try {
            // For iOS, we need to pass just the appId string
            window.cordova.exec(
              () => {
                
                resolve();
              },
              (error: any) => {
                
                reject(error);
              },
              'OneSignalPush', 
              'init',
              [appId] // Pass the appId directly as a string
            );
          } catch (err) {
            
            reject(err);
          }
        });
      }

      if (this.modernSDK5) {
        // SDK 5.x uses a different initialization approach
        
        
        // Try setting the appId property directly
        if (this.plugin.appId !== undefined) {
          
          this.plugin.appId = appId;
          return;
        }
        
        // Try using _appID property
        if (this.plugin._appID) {
          
          if (typeof this.plugin._appID === 'object') {
            this.plugin._appID.value = appId;
          } else {
            this.plugin._appID = appId;
          }
          return;
        }
        
        // For SDK 5.x, OneSignal might still use Cordova bridge but expects just the appId string
        if (window.cordova && window.cordova.exec) {
          
          return new Promise((resolve, reject) => {
            try {
              window.cordova.exec(
                () => {
                  
                  this.isInitialized = true; // Mark as initialized
                  resolve();
                },
                (error: any) => {
                  
                  reject(error);
                },
                'OneSignalPush', 
                'init',
                [appId] // Pass just the appId string, not an object
              );
            } catch (err) {
              
              reject(err);
            }
          });
        }
        
        
      }
      else if (this.legacyMode) {
        // Legacy method was init instead of setAppId
        if (typeof this.plugin.init === 'function') {
          
          await this.plugin.init(appId);
          this.isInitialized = true; // Mark as initialized
          return;
        }
      }
      // Standard method
      else if (typeof this.plugin.setAppId === 'function') {
        
        await this.plugin.setAppId(appId);
        this.isInitialized = true; // Mark as initialized
        return;
      }
      
      // Fallback to direct property setting
      
      this.plugin.appId = appId;
      this.isInitialized = true; // Mark as initialized
    } catch (error) {
      
      throw error;
    }
  }
  
  async getDeviceState(): Promise<any> {
    if (!this.isAvailable()) return null;
    
    // Check if OneSignal is properly initialized before calling any methods
    if (!this.isInitialized) {
      
      return null;
    }
    
    try {
      // Create a timeout wrapper to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('getDeviceState timeout')), 5000);
      });

      // Special handling for Cordova OneSignal plugin with legacy mode
      if (this.legacyMode && window.cordova && window.cordova.exec) {
        
        const legacyPromise = new Promise((resolve) => {
          try {
            window.cordova.exec(
              (result: any) => {
                
                resolve(result);
              },
              (error: any) => {
                
                resolve(null);
              },
              'OneSignalPush', 
              'getPermissionSubscriptionState',
              []
            );
          } catch (err) {
            
            resolve(null);
          }
        });

        return await Promise.race([legacyPromise, timeoutPromise]);
      }
      
      if (this.modernSDK5) {
        // SDK 5.x uses User.getPushSubscription() instead of getDeviceState()
        
        
        if (this.plugin.User && typeof this.plugin.User.getPushSubscription === 'function') {
          const subscriptionPromise = this.plugin.User.getPushSubscription();
          const subscription = await Promise.race([subscriptionPromise, timeoutPromise]);
          
          
          // Convert SDK 5.x format to legacy format for compatibility
          return {
            userId: subscription?.id || subscription?.onesignalId,
            hasNotificationPermission: subscription?.optedIn || false,
            isSubscribed: subscription?.optedIn || false,
            pushToken: subscription?.token,
            externalUserId: subscription?.externalUserId || subscription?.externalId
          };
        }
        
        // Try alternative SDK 5.x methods with timeout protection
        if (this.plugin.User && typeof this.plugin.User.getOnesignalId === 'function') {
          
          
          try {
            const onesignalIdPromise = this.plugin.User.getOnesignalId();
            const onesignalId = await Promise.race([onesignalIdPromise, timeoutPromise]);
            
            // Also try to get permission status with timeout
            let hasPermission = false;
            let externalUserId = null;
            
            if (this.plugin.Notifications && typeof this.plugin.Notifications.getPermission === 'function') {
              try {
                const permissionPromise = this.plugin.Notifications.getPermission();
                hasPermission = await Promise.race([permissionPromise, timeoutPromise]);
              } catch (permError) {
                
              }
            }

            // Try to get external user ID
            if (this.plugin.User && typeof this.plugin.User.getExternalUserId === 'function') {
              try {
                const externalIdPromise = this.plugin.User.getExternalUserId();
                externalUserId = await Promise.race([externalIdPromise, timeoutPromise]);
              } catch (extError) {
                
              }
            }
            
            return {
              userId: onesignalId,
              hasNotificationPermission: hasPermission,
              isSubscribed: hasPermission,
              externalUserId: externalUserId
            };
          } catch (timeoutError) {
            
            return null;
          }
        }
        
        
        return null;
      }
      else if (this.legacyMode) {
        // Legacy method
        if (typeof this.plugin.getPermissionSubscriptionState === 'function') {
          
          const legacyPromise = this.plugin.getPermissionSubscriptionState();
          return await Promise.race([legacyPromise, timeoutPromise]);
        }
      }
      // Standard method
      else if (typeof this.plugin.getDeviceState === 'function') {
        
        const deviceStatePromise = this.plugin.getDeviceState();
        return await Promise.race([deviceStatePromise, timeoutPromise]);
      }
      
      
      return null;
    } catch (error) {
      
      return null;
    }
  }
  
  async disablePush(disable: boolean): Promise<void> {
    if (!this.isAvailable()) return;
    
    try {
      // Special handling for Cordova OneSignal plugin with legacy mode
      if (this.legacyMode && window.cordova && window.cordova.exec) {
        
        return new Promise((resolve, reject) => {
          try {
            // Use setSubscription which is the legacy method name
            // It takes the opposite boolean - true to enable, false to disable
            window.cordova.exec(
              () => {
                
                resolve();
              },
              (error: any) => {
                
                reject(error);
              },
              'OneSignalPush', 
              'setSubscription',
              [!disable] // Invert the value - true to enable, false to disable
            );
          } catch (err) {
            
            reject(err);
          }
        });
      }
      
      if (this.modernSDK5) {
        // SDK 5.x uses a different approach
        if (this.plugin.Notifications && typeof this.plugin.Notifications.disablePush === 'function') {
          await this.plugin.Notifications.disablePush(disable);
          return;
        }
      }
      else if (this.legacyMode) {
        // Legacy method was setSubscription(true) to enable, setSubscription(false) to disable
        if (typeof this.plugin.setSubscription === 'function') {
          await this.plugin.setSubscription(!disable);
          
          return;
        }
      }
      // Standard method
      else if (typeof this.plugin.disablePush === 'function') {
        await this.plugin.disablePush(disable);
        return;
      }
      
      
    } catch (error) {
      
    }
  }
  
  async promptForPushNotificationsWithUserResponse(fallbackToSettings: boolean): Promise<boolean> {
    if (!this.isAvailable()) return false;
    
    // Check if OneSignal is properly initialized before prompting for permissions
    if (!this.isInitialized) {
      
      return false;
    }
    
    try {
      // Special handling for Cordova OneSignal plugin with legacy mode
      if (this.legacyMode && window.cordova && window.cordova.exec) {
        
        return new Promise((resolve) => {
          try {
            window.cordova.exec(
              (result: any) => {
                
                // The result could be true, false, or undefined depending on implementation
                // In most Cordova plugins this returns 1 for accepted, 0 for denied
                resolve(result === true || result === 1);
              },
              (error: any) => {
                
                resolve(false);
              },
              'OneSignalPush', 
              'promptForPushNotificationsWithUserResponse',
              [fallbackToSettings]
            );
          } catch (err) {
            
            resolve(false);
          }
        });
      }

      // SDK 5.x structure
      if (this.modernSDK5) {
        
        
        if (this.plugin.Notifications && typeof this.plugin.Notifications.requestPermission === 'function') {
          
          const result = await this.plugin.Notifications.requestPermission();
          
          return result === true;
        }
        
        // Try alternative SDK 5.x methods
        if (this.plugin.Notifications && typeof this.plugin.Notifications.permissionNative === 'function') {
          
          const result = await this.plugin.Notifications.permissionNative();
          return result === true;
        }
        
        
        return false;
      }
      
      // Standard method - SDK 3.x and 4.x
      if (typeof this.plugin.promptForPushNotificationsWithUserResponse === 'function') {
        
        const result = await this.plugin.promptForPushNotificationsWithUserResponse(fallbackToSettings);
        return result === true;
      }
      
      
      return false;
    } catch (error) {
      
      return false;
    }
  }
  
  setNotificationWillShowInForegroundHandler(handler: (event: any) => void): void {
    if (!this.isAvailable()) return;
    
    try {
      // Special handling for Cordova OneSignal plugin with legacy mode
      if (this.legacyMode && window.cordova && window.cordova.exec) {
        
        try {
          window.cordova.exec(
            (notificationData: any) => {
              
              if (handler) handler(notificationData);
            },
            (error: any) => {
              
            },
            'OneSignalPush', 
            'handleNotificationReceived', // This is the correct legacy method name
            []
          );
        } catch (err) {
          
        }
        return;
      }
      
      if (this.modernSDK5) {
        // SDK 5.x uses Notifications namespace
        if (this.plugin.Notifications && typeof this.plugin.Notifications.setForegroundWillDisplayHandler === 'function') {
          this.plugin.Notifications.setForegroundWillDisplayHandler(handler);
          return;
        }
      }
      else if (this.legacyMode) {
        // Legacy method was handleNotificationReceived
        if (typeof this.plugin.handleNotificationReceived === 'function') {
          this.plugin.handleNotificationReceived(handler);
          return;
        }
      }
      // Standard method
      else if (typeof this.plugin.setNotificationWillShowInForegroundHandler === 'function') {
        this.plugin.setNotificationWillShowInForegroundHandler(handler);
        return;
      }
      
      
    } catch (error) {
      
    }
  }
  
  setNotificationOpenedHandler(handler: (event: any) => void): void {
    if (!this.isAvailable()) return;
    
    try {
      // Special handling for Cordova OneSignal plugin with legacy mode
      if (this.legacyMode && window.cordova && window.cordova.exec) {
        
        try {
          window.cordova.exec(
            (openedData: any) => {
              
              if (handler) handler(openedData);
            },
            (error: any) => {
              
            },
            'OneSignalPush', 
            'handleNotificationOpened', // This is the correct legacy method name
            []
          );
        } catch (err) {
          
        }
        return;
      }
      
      if (this.modernSDK5) {
        // SDK 5.x uses Notifications namespace
        if (this.plugin.Notifications && typeof this.plugin.Notifications.setNotificationOpenedHandler === 'function') {
          this.plugin.Notifications.setNotificationOpenedHandler(handler);
          return;
        }
      }
      else if (this.legacyMode) {
        // Legacy method was handleNotificationOpened
        if (typeof this.plugin.handleNotificationOpened === 'function') {
          this.plugin.handleNotificationOpened(handler);
          return;
        }
      }
      // Standard method
      else if (typeof this.plugin.setNotificationOpenedHandler === 'function') {
        this.plugin.setNotificationOpenedHandler(handler);
        return;
      }
      
      
    } catch (error) {
      
    }
  }

  /**
   * Set External ID for user identification
   * This is crucial to prevent multiple OneSignal users from being created
   */
  async login(externalId: string): Promise<void> {
    if (!this.isAvailable()) return;
    
    // Check if OneSignal is properly initialized before calling login methods
    if (!this.isInitialized) {
      
      return;
    }
    
    try {
      
      
      // Helper function to add timeout to promises
      const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, methodName: string): Promise<T> => {
        return Promise.race([
          promise,
          new Promise<T>((_, reject) => 
            setTimeout(() => reject(new Error(`${methodName} timed out after ${timeoutMs}ms`)), timeoutMs)
          )
        ]);
      };
      
      // Check if OneSignal is properly initialized before attempting login
      const isInitialized = await this.checkInitialization();
      if (!isInitialized) {
        
        // Try to initialize with a short delay
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      if (this.modernSDK5) {
        // SDK 5.x approach with improved compatibility and faster timeouts
        
        
        // Method 1: Try User.login() with reduced timeout and better error handling
        if (this.plugin.User && typeof this.plugin.User.login === 'function') {
          try {
            
            await new Promise(resolve => setTimeout(resolve, 300)); // Shorter wait
            await withTimeout(this.plugin.User.login(externalId), 4000, 'User.login()'); // Reduced from 8000ms
            
            
            // Quick verification
            await new Promise(resolve => setTimeout(resolve, 500)); // Shorter wait
            const deviceState = await withTimeout(this.getDeviceState(), 3000, 'getDeviceState');
            if (deviceState?.externalUserId === externalId) {
              
              return;
            } else {
              
            }
          } catch (userLoginError) {
            
          }
        }
        
        // Method 2: Try Cordova bridge methods with reduced timeouts
        if (window.cordova && window.cordova.exec) {
          
          
          // First, try the newer "login" method with reduced timeout
          try {
            
            const cordovaLoginPromise = new Promise<void>((resolve, reject) => {
              const timeoutId = setTimeout(() => {
                reject(new Error('Cordova login method timeout'));
              }, 4000); // Reduced from 8000ms
              
              window.cordova.exec(
                (result: any) => {
                  clearTimeout(timeoutId);
                  
                  resolve();
                },
                (error: any) => {
                  clearTimeout(timeoutId);
                  
                  reject(error);
                },
                'OneSignalPush', 
                'login',
                [externalId]
              );
            });
            
            await cordovaLoginPromise;
            
            // Quick verify login success
            await new Promise(resolve => setTimeout(resolve, 500));
            const deviceState = await withTimeout(this.getDeviceState(), 3000, 'getDeviceState');
            if (deviceState?.externalUserId === externalId) {
              
              return;
            }
          } catch (loginError) {
            
          }
          
          // Method 3: Try the direct method call that should work with iOS plugin (setExternalId)
          try {
            
            const setExternalIdPromise = new Promise<void>((resolve, reject) => {
              const timeoutId = setTimeout(() => {
                reject(new Error('setExternalId timeout'));
              }, 3000);
              
              window.cordova.exec(
                (result: any) => {
                  clearTimeout(timeoutId);
                  
                  resolve();
                },
                (error: any) => {
                  clearTimeout(timeoutId);
                  
                  reject(error);
                },
                'OneSignalPush', 
                'setExternalId',
                [externalId]
              );
            });
            
            await setExternalIdPromise;
            
            // Quick verify
            await new Promise(resolve => setTimeout(resolve, 500));
            const deviceState = await withTimeout(this.getDeviceState(), 3000, 'getDeviceState');
            if (deviceState?.externalUserId === externalId) {
              
              return;
            }
          } catch (setExternalIdError) {
            
          }
          

        }

        
        // Final fallback: Try User.setExternalUserId if available
        if (this.plugin.User && typeof this.plugin.User.setExternalUserId === 'function') {
          
          try {
            await withTimeout(this.plugin.User.setExternalUserId(externalId), 3000, 'User.setExternalUserId()');
            
            return;
          } catch (setExternalError) {
            
          }
        }
        
        
        throw new Error('All SDK 5.x login methods failed - OneSignal SDK may have compatibility issues');
      }
      
      // Legacy SDK handling (3.x/4.x)
      else if (this.legacyMode && window.cordova && window.cordova.exec) {
        
        const legacyLoginPromise = new Promise<void>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error('Legacy login timeout'));
          }, 5000); // Reduced timeout
          
          try {
            window.cordova.exec(
              (result: any) => {
                clearTimeout(timeoutId);
                
                resolve();
              },
              (error: any) => {
                clearTimeout(timeoutId);
                
                reject(error);
              },
              'OneSignalPush', 
              'setExternalUserId',
              [externalId]
            );
          } catch (err) {
            clearTimeout(timeoutId);
            
            reject(err);
          }
        });
        
        await legacyLoginPromise;
        return;
      }
      
      // Standard method for SDK 3.x/4.x
      else if (typeof this.plugin.setExternalUserId === 'function') {
        
        await withTimeout(this.plugin.setExternalUserId(externalId), 5000, 'Standard setExternalUserId'); // Reduced timeout
        return;
      }
      
      
      throw new Error('No compatible login method found for OneSignal');
    } catch (error) {
      
      throw error;
    }
  }

  /**
   * Check if OneSignal is properly initialized
   */
  private async checkInitialization(): Promise<boolean> {
    try {
      if (!this.isAvailable()) return false;
      
      // Try to get device state as a way to verify initialization
      const deviceState = await Promise.race([
        this.getDeviceState(),
        new Promise<null>((_, reject) => 
          setTimeout(() => reject(new Error('Initialization check timeout')), 3000)
        )
      ]);
      
      return deviceState !== null;
    } catch (error) {
      
      return false;
    }
  }

  /**
   * Remove External ID (logout user)
   */
  async logout(): Promise<void> {
    if (!this.isAvailable()) return;
    
    // Check if OneSignal is properly initialized before calling logout methods
    if (!this.isInitialized) {
      
      return;
    }
    
    try {
      
      
      if (this.modernSDK5) {
        // SDK 5.x uses User.logout() method - try this first
        
        
        if (this.plugin.User && typeof this.plugin.User.logout === 'function') {
          try {
            await this.plugin.User.logout();
            
            return;
          } catch (userLogoutError) {
            
            // Continue to try Cordova bridge
          }
        }
        
        // For SDK 5.x, try Cordova bridge with correct method name
        if (window.cordova && window.cordova.exec) {
          
          
          // Try "logout" method first (SDK 5.x style)
          try {
            
            await new Promise((resolve, reject) => {
              window.cordova.exec(
                () => {
                  
                  resolve(undefined);
                },
                (error: any) => {
                  
                  reject(error);
                },
                'OneSignalPush', 
                'logout',
                []
              );
            });
            return;
          } catch (logoutError) {
            
          }
          
          // Fallback to removeExternalUserId method (if available)
          try {
            
            await new Promise((resolve, reject) => {
              window.cordova.exec(
                () => {
                  
                  resolve(undefined);
                },
                (error: any) => {
                  
                  reject(error);
                },
                'OneSignalPush', 
                'removeExternalUserId',
                []
              );
            });
            return;
          } catch (removeExternalError) {
            
          }
        }
        
        
        throw new Error('All SDK 5.x logout methods failed');
      }
      
      // Special handling for Cordova OneSignal plugin with legacy mode
      else if (this.legacyMode && window.cordova && window.cordova.exec) {
        
        return new Promise((resolve, reject) => {
          try {
            window.cordova.exec(
              () => {
                
                resolve();
              },
              (error: any) => {
                
                reject(error);
              },
              'OneSignalPush', 
              'removeExternalUserId',
              []
            );
          } catch (err) {
            
            reject(err);
          }
        });
      }
      
      // Standard method for SDK 3.x/4.x
      else if (typeof this.plugin.removeExternalUserId === 'function') {
        
        await this.plugin.removeExternalUserId();
        return;
      }
      
      
    } catch (error) {
      
      throw error;
    }
  }

  /**
   * Debug method to detect available OneSignal methods
   * This helps troubleshoot which methods are actually available in the plugin
   */
  detectAvailableMethods(): { [key: string]: any } {
    if (!this.isAvailable()) return {};
    
    const methods: { [key: string]: any } = {};
    
    // Check plugin methods
    if (this.plugin) {
      methods.pluginMethods = Object.getOwnPropertyNames(this.plugin)
        .filter(name => typeof this.plugin[name] === 'function');
      
      // Check User namespace
      if (this.plugin.User) {
        methods.userMethods = Object.getOwnPropertyNames(this.plugin.User)
          .filter(name => typeof this.plugin.User[name] === 'function');
      }
      
      // Check Notifications namespace
      if (this.plugin.Notifications) {
        methods.notificationsMethods = Object.getOwnPropertyNames(this.plugin.Notifications)
          .filter(name => typeof this.plugin.Notifications[name] === 'function');
      }
    }
    
    // Check Cordova methods by trying to inspect the native plugin
    if (window.cordova && window.cordova.exec) {
      methods.cordovaMethods = [
        'login',
        'logout', 
        'setExternalUserId',
        'setExternalId',
        'addAlias',
        'removeAlias',
        'User.login',
        'User.logout',
        'User.addAlias',
        'User.removeAlias',
        'setAppId',
        'getDeviceState',
        'promptForPushNotificationsWithUserResponse'
      ];
      
      // Skip excessive method testing to avoid debug spam
    }
    
    return methods;
  }
}

// Interface for notification data
export interface OSNotificationPayload {
  title: string;
  body: string;
  additionalData?: Record<string, any>;
  notificationId: string;
  // Add more fields based on OneSignal documentation as needed
}

export interface OSNotification {
  complete: (action: OSNotificationAction) => void;
  payload: OSNotificationPayload;
}

export enum OSNotificationAction {
  Dismiss = 0,
  Display = 1
}

// Create a global OneSignal adapter instance
let oneSignalAdapter: OneSignalAdapter | null = null;

export const getOneSignalAdapter = (): OneSignalAdapter => {
  if (!oneSignalAdapter) {
    oneSignalAdapter = new OneSignalAdapter();
  }
  return oneSignalAdapter;
}

/**
 * Initialize OneSignal SDK without requesting permission
 * This should be called on app startup before login
 */
export const initializeOneSignalWithoutPermission = async (): Promise<void> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return;
    }

    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    
    if (!oneSignalAppId) {
      
      throw new Error('VITE_ONESIGNAL_APP_ID environment variable not set');
    }

    

    // Check basic plugin availability first
    if (!window.plugins) {
      
      throw new Error('window.plugins not available - Cordova plugins not loaded');
    }

    if (!window.plugins.OneSignal) {
      
      
      throw new Error('OneSignal plugin not found - plugin may not be installed');
    }

    
    

    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      throw new Error('OneSignal adapter not available');
    }

    
    
    // Initialize OneSignal using the adapter (this just sets up the SDK)
    try {
      await adapter.setAppId(oneSignalAppId);
      
    } catch (setAppIdError) {
      
      throw new Error(`Failed to set OneSignal app ID: ${setAppIdError instanceof Error ? setAppIdError.message : String(setAppIdError)}`);
    }
    
    
    
    // Test if we can get device state
    try {
      const testDeviceState = await adapter.getDeviceState();
      
    } catch (deviceStateError) {
      
    }
    
    
    
    // Set notification handlers (but don't request permission)
    try {
      adapter.setNotificationWillShowInForegroundHandler((notificationReceivedEvent: any) => {
        
        
        try {
          // Complete with notification action to show the notification
          if (notificationReceivedEvent?.notification?.complete) {
            notificationReceivedEvent.notification.complete(OSNotificationAction.Display);
          }
        } catch (error) {
          
        }
      });

      adapter.setNotificationOpenedHandler((openedEvent: any) => {
        
        
        try {
          // Handle opened notification here
          const data = openedEvent?.notification?.additionalData;
          if (data) {
            // Handle different notification types based on data
            handleNotificationOpened(openedEvent.notification);
          }
        } catch (error) {
          
        }
      });
      
      
    } catch (handlerError) {
      
      // Don't throw here, handlers are optional
    }

    
    
  } catch (error) {
    
    throw error; // Re-throw to allow caller to handle
  }
};

/**
 * Initialize OneSignal SDK
 */
export const initializeOneSignal = async (): Promise<void> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return;
    }

    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    
    if (!oneSignalAppId) {
      
      return;
    }

    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return;
    }

    
    
    // Initialize OneSignal using the adapter
    await adapter.setAppId(oneSignalAppId);
    
    
    // Set notification handlers
    adapter.setNotificationWillShowInForegroundHandler((notificationReceivedEvent: any) => {
      
      
      try {
        // Complete with notification action to show the notification
        if (notificationReceivedEvent?.notification?.complete) {
          notificationReceivedEvent.notification.complete(OSNotificationAction.Display);
        }
      } catch (error) {
        
      }
    });

    adapter.setNotificationOpenedHandler((openedEvent: any) => {
      
      
      try {
        // Handle opened notification here
        // You could dispatch an event or call a function based on notification data
        const data = openedEvent?.notification?.additionalData;
        if (data) {
          // Handle different notification types based on data
          handleNotificationOpened(openedEvent.notification);
        }
      } catch (error) {
        
      }
    });

    // Prompt for push notification permission
    const hasPermission = await requestPushPermission();
    
    
    if (hasPermission) {
      // Get and save device state (which includes the OneSignal User ID)
      const deviceState = await adapter.getDeviceState();
      
      
      if (deviceState?.userId) {
        // Save the OneSignal user ID to your backend
        await saveOneSignalUserId(deviceState.userId);
      } else {
        
      }
    }
    
    
  } catch (error) {
    
    throw error; // Re-throw to allow caller to handle
  }
};

/**
 * Initialize OneSignal for a specific user (e.g., after registration)
 * This function ensures OneSignal ID is saved even without permission prompt
 */
export const initializeOneSignalForUser = async (): Promise<void> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return;
    }

    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    
    if (!oneSignalAppId) {
      
      return;
    }

    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return;
    }

    
    
    // Initialize OneSignal using the adapter if not already initialized
    await adapter.setAppId(oneSignalAppId);
    
    // Get device state to extract OneSignal User ID
    const deviceState = await adapter.getDeviceState();
    
    
    if (deviceState?.userId) {
      // Save the OneSignal user ID immediately, even without permission
      await saveOneSignalUserId(deviceState.userId);
      
    } else {
      
      // If no user ID yet, we'll try to get it after a short delay
      setTimeout(async () => {
        try {
          const retryDeviceState = await adapter.getDeviceState();
          if (retryDeviceState?.userId) {
            await saveOneSignalUserId(retryDeviceState.userId);
            
          }
        } catch (retryError) {
          
        }
      }, 2000);
    }
    
    
  } catch (error) {
    
    throw error; // Re-throw to allow caller to handle
  }
};

/**
 * Request push notification permission
 */
export const requestPushPermission = async (): Promise<boolean> => {
  try {
    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return false;
    }
    
    
    return await adapter.promptForPushNotificationsWithUserResponse(true);
  } catch (error) {
    
    return false;
  }
};

/**
 * Save OneSignal User ID to your backend
 */
export const saveOneSignalUserId = async (userId: string): Promise<boolean> => {
  try {
    if (!userId) {
      
      return false;
    }
    
    
    
    // Import dynamically to avoid circular dependencies
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      
      return false;
    }

    
    const { error } = await supabase
      .from('users')
      .update({ onesignal_user_id: userId })
      .eq('id', user.id);

    if (error) {
      
      return false;
    }
    
    
    return true;
  } catch (error) {
    
    return false;
  }
};

/**
 * Enhanced notification permission status check
 * Uses multiple methods to accurately determine permission status
 */
export const getEnhancedNotificationPermissionStatus = async (): Promise<{
  hasPermission: boolean;
  source: string;
  details: any;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return { hasPermission: false, source: 'platform', details: { platform: 'web' } };
    }

    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { hasPermission: false, source: 'adapter', details: { available: false } };
    }

    let oneSignalPermission = false;
    let oneSignalDetails: any = {};

    // Method 1: Try OneSignal's device state
    try {
      const deviceState = await adapter.getDeviceState();
      
      oneSignalPermission = !!deviceState?.hasNotificationPermission;
      oneSignalDetails = deviceState;
    } catch (error) {
      
    }

    // Method 2: Try direct OneSignal Notifications.getPermission for SDK 5.x
    let directOneSignalPermission = false;
    try {
      const plugin = adapter.getPlugin();
      if (plugin?.Notifications && typeof plugin.Notifications.getPermission === 'function') {
        directOneSignalPermission = await plugin.Notifications.getPermission();
        
      }
    } catch (error) {
      
    }

    // Method 3: Use native Capacitor LocalNotifications as backup
    let nativePermission = false;
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const permissionStatus = await LocalNotifications.checkPermissions();
      nativePermission = permissionStatus.display === 'granted';
      
    } catch (error) {
      
    }

    // Determine final result with priority: native > direct OneSignal > OneSignal device state
    let finalPermission = false;
    let source = 'unknown';

    if (nativePermission) {
      finalPermission = true;
      source = 'native';
      
    } else if (directOneSignalPermission) {
      finalPermission = true;
      source = 'onesignal_direct';
      
    } else if (oneSignalPermission) {
      finalPermission = true;
      source = 'onesignal_state';
      
    } else {
      finalPermission = false;
      source = 'all_methods';
      
    }

    const details = {
      oneSignalPermission,
      directOneSignalPermission,
      nativePermission,
      oneSignalDetails
    };

    

    return {
      hasPermission: finalPermission,
      source,
      details
    };

  } catch (error) {
    
    return {
      hasPermission: false,
      source: 'error',
      details: { error: error instanceof Error ? error.message : String(error) }
    };
  }
};

/**
 * Get the current device notification permission status
 * Now uses native permissions as source of truth and auto-syncs OneSignal
 */
export const getNotificationPermissionStatus = async (): Promise<boolean> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return false;
    }
    
    // STEP 1: Check native device permission (most reliable)
    let nativePermission = false;
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<any>((_, reject) => {
        setTimeout(() => reject(new Error('Native permission check timeout')), 3000);
      });
      
      const permissionPromise = LocalNotifications.checkPermissions();
      const permissionStatus = await Promise.race([permissionPromise, timeoutPromise]);
      nativePermission = permissionStatus.display === 'granted';
      
    } catch (error) {
      
    }
    
    // STEP 2: Check OneSignal state with timeout protection
    const adapter = getOneSignalAdapter();
    let oneSignalPermission = false;
    
    if (adapter.isAvailable()) {
      try {
        // Add timeout specifically for OneSignal operations
        const oneSignalTimeout = new Promise<any>((_, reject) => {
          setTimeout(() => reject(new Error('OneSignal check timeout')), 5000);
        });
        
        const deviceStatePromise = adapter.getDeviceState();
        const deviceState = await Promise.race([deviceStatePromise, oneSignalTimeout]);
        
        if (deviceState) {
          oneSignalPermission = !!deviceState?.hasNotificationPermission;
          
          
          // STEP 3: Auto-sync if there's a discrepancy and native permission is granted
          if (nativePermission && !oneSignalPermission) {
            
            
            try {
              // Force OneSignal to sync with native permission with timeout
              const syncTimeout = new Promise<void>((_, reject) => {
                setTimeout(() => reject(new Error('Sync timeout')), 3000);
              });
              
              const syncPromise = adapter.disablePush(false); // Enable push to trigger sync
              await Promise.race([syncPromise, syncTimeout]);
              
              // Re-check OneSignal state after sync with timeout
              await new Promise(resolve => setTimeout(resolve, 1000));
              const updatedStatePromise = adapter.getDeviceState();
              const updatedDeviceState = await Promise.race([updatedStatePromise, oneSignalTimeout]);
              
              if (updatedDeviceState) {
                oneSignalPermission = !!updatedDeviceState?.hasNotificationPermission;
                
                
                // Update database if sync worked and we have user ID
                if (oneSignalPermission && updatedDeviceState?.userId) {
                  try {
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user) {
                      await supabase
                        .from('users')
                        .update({
                          notification_enabled: true,
                          notification_permission_asked: true,
                          onesignal_user_id: updatedDeviceState.userId,
                          updated_at: new Date().toISOString()
                        })
                        .eq('id', user.id);
                      
                      
                    }
                  } catch (dbError) {
                    
                  }
                }
              }
            } catch (syncError) {
              
            }
          }
        } else {
          
        }
      } catch (error) {
        
        
        // If OneSignal is not responding, we should still return native permission
        
      }
    } else {
      
    }
    
    // STEP 4: Return native permission as source of truth
    const finalResult = nativePermission;
    
    
    return finalResult;
  } catch (error) {
    
    return false;
  }
};

/**
 * Enable or disable push notifications
 */
export const setPushNotificationsEnabled = async (enabled: boolean): Promise<boolean> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return false;
    }
    
    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return false;
    }
    
    
    // This disables/enables notifications on the OneSignal side
    await adapter.disablePush(!enabled);
    
    // If enabling, make sure we have permission from the device
    if (enabled) {
      
      const hasPermission = await requestPushPermission();
      if (!hasPermission) {
        
        return false;
      }
    }
    
    
    return true;
  } catch (error) {
    
    return false;
  }
};

/**
 * Sync the device notification status with OneSignal
 * Now uses native permissions as source of truth
 */
export const syncNotificationPermissions = async (): Promise<void> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return;
    }
    
    
    
    // Use the improved permission check which automatically handles sync
    const hasPermission = await getNotificationPermissionStatus();
    
    
    
    
  } catch (error) {
    
  }
};

/**
 * Handle different notification actions when a notification is opened
 */
const handleNotificationOpened = (notification: OSNotificationPayload) => {
  try {
    const data = notification.additionalData;
    if (!data) return;
  
    // Example: Handle different notification types
    switch (data.type) {
      case 'payment':
        // Navigate to payment screen or show payment information
        
        // You could use React Router or other navigation system here
        // window.location.href = '/payments';
        break;
      case 'update':
        // Handle app update notification
        
        break;
      default:
        // Handle general notifications
        
    }
  } catch (error) {
    
  }
};

/**
 * Send a notification to specific user IDs
 */
export const sendNotification = async (
  userIds: string[],
  title: string,
  body: string,
  data: Record<string, any> = {}
): Promise<{ success: boolean; message?: string }> => {
  try {
    // This function is commented out as we're now using the OneSignal dashboard for notifications
    // Only check payment reminders will be sent through this API
    
    if (!data.type || data.type !== 'check_payment_reminder') {
      
      return {
        success: false,
        message: 'Only check payment reminders are allowed through this API'
      };
    }
    
    if (!userIds || userIds.length === 0) {
      
      return {
        success: false,
        message: 'No recipients specified'
      };
    }
    
    
    
    // Get OneSignal App ID from environment
    const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    
    if (!appId) {
      
      return {
        success: false,
        message: 'OneSignal App ID not found'
      };
    }
    
    // Using the OneSignal REST API to send notifications
    const url = 'https://api.onesignal.com/notifications?c=push';
    const options = {
      method: 'POST',
      headers: {
        accept: 'application/json',
        Authorization: 'Key YOUR_APP_API_KEY', // Replace with your actual API key
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        app_id: appId,
        contents: { en: body },
        headings: { en: title },
        data: data,
        include_external_user_ids: userIds
      })
    };
    
    const response = await fetch(url, options);
    const result = await response.json();
    
    if (!response.ok) {
      
      return {
        success: false,
        message: result.errors?.[0] || 'Failed to send notification'
      };
    }
    
    
    return { 
      success: true,
      message: 'Check payment reminder sent successfully'
    };
  } catch (error) {
    
    return {
      success: false,
      message: 'Failed to send notification'
    };
  }
};

/**
 * Request notification permission and sync with user (call after login)
 */
export const requestNotificationPermissionForUser = async (): Promise<{
  success: boolean;
  permissionGranted: boolean;
  oneSignalUserId?: string;
  error?: string;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return { success: false, permissionGranted: false, error: 'Not on native platform' };
    }

    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { success: false, permissionGranted: false, error: 'OneSignal adapter not available' };
    }

    
    
    // Request permission from user
    const permissionGranted = await adapter.promptForPushNotificationsWithUserResponse(true);
    
    
    
    if (permissionGranted) {
      // Get device state to extract OneSignal User ID
      const deviceState = await adapter.getDeviceState();
      
      
      if (deviceState?.userId) {
        // Save the OneSignal user ID to backend
        await saveOneSignalUserId(deviceState.userId);
        
        
        return {
          success: true,
          permissionGranted: true,
          oneSignalUserId: deviceState.userId
        };
      } else {
        
        
        // Try again after a short delay
        setTimeout(async () => {
          try {
            const retryDeviceState = await adapter.getDeviceState();
            if (retryDeviceState?.userId) {
              await saveOneSignalUserId(retryDeviceState.userId);
              
            }
          } catch (retryError) {
            
          }
        }, 2000);
        
        return {
          success: true,
          permissionGranted: true,
          oneSignalUserId: undefined
        };
      }
    } else {
      
      return {
        success: true,
        permissionGranted: false
      };
    }
    
  } catch (error) {
    
    return {
      success: false,
      permissionGranted: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Check if OneSignal is properly initialized and ready for use
 */
export const isOneSignalReady = async (): Promise<boolean> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return false;
    }

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return false;
    }

    // Try to get device state to verify OneSignal is working
    try {
      const deviceState = await adapter.getDeviceState();
      
      return deviceState !== null;
    } catch (error) {
      
      return false;
    }
  } catch (error) {
    
    return false;
  }
};

/**
 * Wait for OneSignal to be ready, with timeout
 */
export const waitForOneSignalReady = async (timeoutMs: number = 10000): Promise<boolean> => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < timeoutMs) {
    if (await isOneSignalReady()) {
      
      return true;
    }
    
    // Wait 500ms before checking again
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  
  
  return false;
};

/**
 * Fix OneSignal sync issue when device has permission but OneSignal doesn't detect it
 */
export const fixOneSignalPermissionSync = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return {
        success: false,
        message: 'Not on native platform'
      };
    }

    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      return {
        success: false,
        message: 'OneSignal adapter not available'
      };
    }

    

    // Step 1: Force OneSignal to re-check permissions by disabling and re-enabling
    try {
      
      
      // Disable push temporarily
      await adapter.disablePush(true);
      
      // Wait a moment
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Re-enable push to force sync
      await adapter.disablePush(false);
      
      
    } catch (error) {
      
    }

    // Step 2: For SDK 5.x, try to force subscription refresh
    try {
      
      const plugin = adapter.getPlugin();
      
      if (plugin?.User && typeof plugin.User.pushSubscription === 'object') {
        // Try to refresh the subscription object
        if (typeof plugin.User.pushSubscription.optIn === 'function') {
          await plugin.User.pushSubscription.optIn();
          
        }
      }
    } catch (error) {
      
    }

    // Step 3: Try prompting for permission (this should succeed since device already has it)
    try {
      
      
      // This should immediately succeed since device already has permission
      const permissionResult = await adapter.promptForPushNotificationsWithUserResponse(false);
      
    } catch (error) {
      
    }

    // Step 4: Wait and check if sync worked
    
    await new Promise(resolve => setTimeout(resolve, 2000));

    const deviceState = await adapter.getDeviceState();
    const syncWorked = !!deviceState?.hasNotificationPermission;

    
    

    // Step 5: Update database if sync worked
    if (syncWorked && deviceState?.userId) {
      try {
        
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error: updateError } = await supabase
            .from('users')
            .update({
              notification_enabled: true,
              notification_permission_asked: true,
              onesignal_user_id: deviceState.userId,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);

          if (updateError) {
            
          } else {
            
          }
        }
      } catch (dbError) {
        
      }
    }

    if (syncWorked) {
      return {
        success: true,
        message: 'OneSignal sync fixed successfully! Permission state is now correct.',
        details: {
          oneSignalUserId: deviceState?.userId,
          syncMethod: 'force_refresh'
        }
      };
    } else {
      return {
        success: false,
        message: 'Sync fix attempted but OneSignal still not detecting permission. This might require app restart or manual OS settings check.',
        details: {
          deviceState,
          troubleshooting: 'Try restarting the app or check iOS Settings > [App] > Notifications'
        }
      };
    }

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error during sync fix'
    };
  }
};

/**
 * Complete post-login OneSignal initialization and permission sync
 * This function should be called automatically after successful login
 */
export const initializeOneSignalAfterLogin = async (userId: string): Promise<{
  success: boolean;
  permissionGranted?: boolean;
  oneSignalUserId?: string;
  message: string;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return { success: false, message: 'Not on native platform' };
    }

    

    // Step 1: Initialize OneSignal SDK
    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    if (!oneSignalAppId) {
      
      return { success: false, message: 'OneSignal App ID not configured' };
    }

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { success: false, message: 'OneSignal plugin not available' };
    }

    

    // Initialize OneSignal
    try {
      await adapter.setAppId(oneSignalAppId);
      
    } catch (initError) {
      
      return { success: false, message: 'Failed to initialize OneSignal' };
    }

    // Set up notification handlers
    try {
      adapter.setNotificationWillShowInForegroundHandler((notificationReceivedEvent: any) => {
        
        try {
          if (notificationReceivedEvent?.notification?.complete) {
            notificationReceivedEvent.notification.complete(OSNotificationAction.Display);
          }
        } catch (error) {
          
        }
      });

      adapter.setNotificationOpenedHandler((openedEvent: any) => {
        
        try {
          const data = openedEvent?.notification?.additionalData;
          if (data) {
            handleNotificationOpened(openedEvent.notification);
          }
        } catch (error) {
          
        }
      });
      
      
    } catch (handlerError) {
      
    }

    // Step 2: Handle permissions automatically using the improved permission check
    
    
    // The improved getNotificationPermissionStatus will automatically handle sync issues
    const hasPermission = await getNotificationPermissionStatus();
    

    // Step 3: Get OneSignal user ID and save to database
    let oneSignalUserId: string | undefined;
    try {
      const deviceState = await adapter.getDeviceState();
      oneSignalUserId = deviceState?.userId;

      if (oneSignalUserId) {
        
        await saveOneSignalUserId(oneSignalUserId);
      } else {
        
        
        // Try again after a delay
        setTimeout(async () => {
          try {
            const retryDeviceState = await adapter.getDeviceState();
            if (retryDeviceState?.userId) {
              await saveOneSignalUserId(retryDeviceState.userId);
              
            }
          } catch (retryError) {
            
          }
        }, 2000);
      }
    } catch (deviceStateError) {
      
    }

    // Step 4: Update database with final notification status
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.id === userId) {
        const updateData: any = {
          notification_enabled: hasPermission,
          notification_permission_asked: true,
          updated_at: new Date().toISOString()
        };

        if (oneSignalUserId) {
          updateData.onesignal_user_id = oneSignalUserId;
        }

        const { error: updateError } = await supabase
          .from('users')
          .update(updateData)
          .eq('id', userId);

        if (updateError) {
          
        } else {
          
        }
      }
    } catch (dbError) {
      
    }

    

    return {
      success: true,
      permissionGranted: hasPermission,
      oneSignalUserId,
      message: hasPermission 
        ? 'OneSignal initialized and notifications enabled'
        : 'OneSignal initialized but notifications not enabled'
    };

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error during OneSignal setup'
    };
  }
};

/**
 * Initialize OneSignal with External ID to prevent multiple users
 * This is the CORRECT way to handle OneSignal user identification
 */
export const initializeOneSignalWithExternalId = async (supabaseUserId: string): Promise<{
  success: boolean;
  permissionGranted?: boolean;
  oneSignalUserId?: string;
  message: string;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return { success: false, message: 'Not on native platform' };
    }

    

    // Step 1: Initialize OneSignal SDK
    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    if (!oneSignalAppId) {
      
      return { success: false, message: 'OneSignal App ID not configured' };
    }

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { success: false, message: 'OneSignal plugin not available' };
    }

    

    // Initialize OneSignal first
    try {
      await adapter.setAppId(oneSignalAppId);
      
    } catch (initError) {
      
      return { success: false, message: 'Failed to initialize OneSignal' };
    }

    // Step 2: CRITICAL - Set External ID to bind this device to the Supabase user
    
    try {
      await adapter.login(supabaseUserId);
      
    } catch (loginError) {
      
      // Continue anyway, but log the issue
    }

    // Step 3: Set up notification handlers
    try {
      adapter.setNotificationWillShowInForegroundHandler((notificationReceivedEvent: any) => {
        
        try {
          if (notificationReceivedEvent?.notification?.complete) {
            notificationReceivedEvent.notification.complete(OSNotificationAction.Display);
          }
        } catch (error) {
          
        }
      });

      adapter.setNotificationOpenedHandler((openedEvent: any) => {
        
        try {
          const data = openedEvent?.notification?.additionalData;
          if (data) {
            handleNotificationOpened(openedEvent.notification);
          }
        } catch (error) {
          
        }
      });
      
      
    } catch (handlerError) {
      
    }

    // Step 4: Check and handle notification permissions
    
    const hasPermission = await getNotificationPermissionStatus();
    

    // Step 5: Get OneSignal user ID after External ID is set
    let oneSignalUserId: string | undefined;
    try {
      // Wait a moment for the External ID to be processed
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const deviceState = await adapter.getDeviceState();
      oneSignalUserId = deviceState?.userId;

      

      if (oneSignalUserId) {
        await saveOneSignalUserId(oneSignalUserId);
        
      } else {
        
        
        // Retry after delay
        setTimeout(async () => {
          try {
            const retryDeviceState = await adapter.getDeviceState();
            if (retryDeviceState?.userId) {
              await saveOneSignalUserId(retryDeviceState.userId);
              
            }
          } catch (retryError) {
            
          }
        }, 3000);
      }
    } catch (deviceStateError) {
      
    }

    // Step 6: Update database with final status
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.id === supabaseUserId) {
        const updateData: any = {
          notification_enabled: hasPermission,
          notification_permission_asked: true,
          updated_at: new Date().toISOString()
        };

        if (oneSignalUserId) {
          updateData.onesignal_user_id = oneSignalUserId;
        }

        const { error: updateError } = await supabase
          .from('users')
          .update(updateData)
          .eq('id', supabaseUserId);

        if (updateError) {
          
        } else {
          
        }
      }
    } catch (dbError) {
      
    }

    

    return {
      success: true,
      permissionGranted: hasPermission,
      oneSignalUserId,
      message: `OneSignal initialized with External ID. ${hasPermission ? 'Notifications enabled.' : 'Notifications not enabled.'}`
    };

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error during OneSignal setup with External ID'
    };
  }
};

/**
 * Logout user from OneSignal (remove External ID)
 */
export const logoutOneSignalUser = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return { success: true, message: 'Not on native platform' };
    }

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { success: true, message: 'OneSignal not available' };
    }

    
    
    try {
      await adapter.logout();
      
      return {
        success: true,
        message: 'User logged out from OneSignal successfully'
      };
    } catch (logoutError) {
      // Don't let OneSignal logout errors block the app logout process
      
      return {
        success: true, // Return success even if OneSignal fails to avoid blocking logout
        message: `OneSignal logout failed (${logoutError instanceof Error ? logoutError.message : 'Unknown error'}), but app logout continued`
      };
    }

  } catch (error) {
    
    // Even in case of unexpected errors, don't block the app logout
    return {
      success: true, // Return success to avoid blocking logout
      message: `OneSignal logout error handled gracefully: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * DEBUG: Test External ID setting with current user's real Supabase ID
 */
export const debugTestExternalId = async (): Promise<{
  success: boolean;
  message: string;
  details: any;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return { success: false, message: 'Not on native platform', details: {} };
    }

    // Get current user's Supabase ID
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      return { 
        success: false, 
        message: 'Must be logged in to test External ID', 
        details: { userError } 
      };
    }

    const supabaseUserId = user.id;
    

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      return { success: false, message: 'OneSignal adapter not available', details: {} };
    }

    
    
    // Get plugin details
    const plugin = adapter.getPlugin();
    

    // Test the login method with real Supabase user ID
    
    await adapter.login(supabaseUserId);
    
    // Wait a moment and get device state
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const deviceState = await adapter.getDeviceState();
    

    // Update database with the result
    try {
      if (deviceState?.userId) {
        await saveOneSignalUserId(deviceState.userId);
        
      }

      const updateData: any = {
        notification_enabled: true, // Assume enabled if we got this far
        notification_permission_asked: true,
        updated_at: new Date().toISOString()
      };

      if (deviceState?.userId) {
        updateData.onesignal_user_id = deviceState.userId;
      }

      const { error: updateError } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', supabaseUserId);

      if (updateError) {
        
      }
    } catch (dbError) {
      
    }

    return {
      success: true,
      message: `External ID test completed successfully! Set External ID to your real Supabase user ID. Check OneSignal dashboard for user with External ID: ${supabaseUserId}`,
      details: {
        supabaseUserId,
        externalIdSet: supabaseUserId,
        deviceState: deviceState,
        oneSignalUserId: deviceState?.userId
      }
    };

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error during External ID test',
      details: { error }
    };
  }
};

/**
 * FIXED: Initialize OneSignal with RevenueCat-like External ID binding
 * This prevents anonymous user creation by setting External ID FIRST
 */
export const initializeOneSignalWithProperExternalId = async (supabaseUserId: string): Promise<{
  success: boolean;
  permissionGranted?: boolean;
  oneSignalUserId?: string;
  message: string;
  wasExistingUser?: boolean;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return { success: false, message: 'Not on native platform' };
    }

    

    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    if (!oneSignalAppId) {
      
      return { success: false, message: 'OneSignal App ID not configured' };
    }

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { success: false, message: 'OneSignal plugin not available' };
    }

    let wasExistingUser = false;
    let needsExternalIdBinding = false;
    let bindingSuccessful = false;

    // Step 1: Enhanced existing state check with better error handling
    try {
      
      
      // Try to get device state with timeout protection
      const deviceStatePromise = adapter.getDeviceState();
      const timeoutPromise = new Promise<null>((_, reject) => 
        setTimeout(() => reject(new Error('Device state check timeout')), 5000)
      );
      
      const deviceState = await Promise.race([deviceStatePromise, timeoutPromise]);
      
      if (deviceState?.userId) {
        const currentExternalId = deviceState.externalUserId;
        
        
        if (currentExternalId === supabaseUserId) {
          
          wasExistingUser = true;
        } else if (currentExternalId && currentExternalId !== supabaseUserId) {
          
          
          
          // Logout the current user with enhanced error handling
          try {
            await adapter.logout();
            await new Promise(resolve => setTimeout(resolve, 2000)); // Longer wait for logout
            needsExternalIdBinding = true;
          } catch (logoutError) {
            
            needsExternalIdBinding = true;
          }
        } else if (!currentExternalId) {
          
          needsExternalIdBinding = true;
        }
      } else {
        
        needsExternalIdBinding = true;
      }
    } catch (checkError) {
      
      needsExternalIdBinding = true;
    }

    if (wasExistingUser) {
      
    } else {
      // Step 2: Enhanced initialization and binding process
      
      
      if (needsExternalIdBinding) {
        // Pre-binding attempt with improved error handling
        try {
          
          
          // Wait a bit longer before attempting pre-binding
          await new Promise(resolve => setTimeout(resolve, 1000));
          await adapter.login(supabaseUserId);
          
          
        } catch (preBindError) {
          
        }
      }

      // SDK initialization with enhanced error handling
      try {
        
        await adapter.setAppId(oneSignalAppId);
        
        // Give the SDK more time to initialize properly
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        
      } catch (initError) {
        
        return { success: false, message: 'Failed to initialize OneSignal SDK' };
      }

      // Enhanced External ID binding with multiple strategies and graceful failure handling
      
      
      const availableMethods = adapter.detectAvailableMethods();
      

      let bindingAttempts = 0;
      const maxBindingAttempts = 3; // Reduced attempts to prevent hanging
      let lastBindingError: Error | null = null;
      
      while (bindingAttempts < maxBindingAttempts && !bindingSuccessful) {
        try {
          bindingAttempts++;
          
          
          // Shorter delays to prevent hanging
          const attemptDelay = 500 + (bindingAttempts * 500);
          await new Promise(resolve => setTimeout(resolve, attemptDelay));
          
          // Use a timeout wrapper for the entire binding process
          const bindingPromise = adapter.login(supabaseUserId);
          const timeoutPromise = new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Binding attempt timed out')), 5000)
          );
          
          await Promise.race([bindingPromise, timeoutPromise]);
          
          // Quick verification with timeout
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const verificationPromise = adapter.getDeviceState();
          const verificationTimeout = new Promise<null>((_, reject) => 
            setTimeout(() => reject(new Error('Verification timed out')), 3000)
          );
          
          const verificationState = await Promise.race([verificationPromise, verificationTimeout]);
          
          if (verificationState?.externalUserId === supabaseUserId) {
            
            bindingSuccessful = true;
          } else {
            
            lastBindingError = new Error(`External ID not set correctly. Expected: ${supabaseUserId}, Got: ${verificationState?.externalUserId}`);
          }
        } catch (bindError) {
          lastBindingError = bindError instanceof Error ? bindError : new Error(String(bindError));
          
          
          // Check for specific SDK 5.x compatibility errors
          if (bindError instanceof Error && (
            bindError.message.includes('does not respond to method call') ||
            bindError.message.includes('timed out') ||
            bindError.message.includes('Binding attempt timed out')
          )) {
            
            
            
            // Don't fail immediately on timeout/compatibility issues, try other methods
            if (bindingAttempts >= maxBindingAttempts) {
              
              break;
            }
          }
        }
      }

      // If binding failed, continue with OneSignal setup but log the issue
      if (!bindingSuccessful) {
        
        
        
        
        
        // Continue with setup but mark as partial success
        // The app should still work for notifications, just without proper user linking
      } else {
        
      }
    }

    // Step 3: Set up notification handlers with error protection
    try {
      adapter.setNotificationWillShowInForegroundHandler((notificationReceivedEvent: any) => {
        
        try {
          if (notificationReceivedEvent?.notification?.complete) {
            notificationReceivedEvent.notification.complete(OSNotificationAction.Display);
          }
        } catch (error) {
          
        }
      });

      adapter.setNotificationOpenedHandler((openedEvent: any) => {
        
        try {
          const data = openedEvent?.notification?.additionalData;
          if (data) {
            handleNotificationOpened(openedEvent.notification);
          }
        } catch (error) {
          
        }
      });
      
      
    } catch (handlerError) {
      
    }

    // Step 4: Get final device state and permissions with enhanced handling
    
    
    // Wait for everything to settle with longer delay
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    let oneSignalUserId: string | undefined;
    let finalExternalId: string | undefined;
    
    try {
      const finalDeviceState = await adapter.getDeviceState();
      oneSignalUserId = finalDeviceState?.userId;
      finalExternalId = finalDeviceState?.externalUserId;
      
      

      if (oneSignalUserId) {
        await saveOneSignalUserId(oneSignalUserId);
        
      }
    } catch (stateError) {
      
    }

    // Step 5: Check permission status with enhanced method
    const hasPermission = await getNotificationPermissionStatus();
    

    // Step 6: Update database with comprehensive status
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const updateData: any = {
          notification_enabled: hasPermission,
          notification_permission_asked: true,
          updated_at: new Date().toISOString()
        };

        if (oneSignalUserId) {
          updateData.onesignal_user_id = oneSignalUserId;
        }

        // Add external ID verification status
        const externalIdVerified = finalExternalId === supabaseUserId;
        updateData.onesignal_external_id_verified = externalIdVerified;

        const { error: updateError } = await supabase
          .from('users')
          .update(updateData)
          .eq('id', user.id);

        if (updateError) {
          
        } else {
          
        }
      }
    } catch (dbError) {
      
    }

    // Step 7: Return comprehensive result with external ID status
    const externalIdVerified = finalExternalId === supabaseUserId;
    const isPartialSuccess = !bindingSuccessful || !externalIdVerified;
    
    const result = {
      success: true, // Always return success if OneSignal is working, even with partial external ID issues
      permissionGranted: hasPermission,
      oneSignalUserId,
      wasExistingUser,
      externalIdSet: externalIdVerified,
      message: wasExistingUser 
        ? 'OneSignal user already properly configured' 
        : isPartialSuccess 
          ? 'OneSignal initialized successfully - External ID may need manual retry'
          : 'OneSignal initialization completed successfully'
    };

    

    return result;

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown initialization error'
    };
  }
};

/**
 * Quick OneSignal diagnostic and fix function
 * Use this when OneSignal appears to be hanging or unresponsive
 */
export const quickOneSignalDiagnosticAndFix = async (): Promise<{
  success: boolean;
  message: string;
  details: any;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return { 
        success: false, 
        message: 'Not on native platform', 
        details: { platform: 'web' } 
      };
    }

    
    
    const adapter = getOneSignalAdapter();
    const details: any = {
      adapterAvailable: adapter.isAvailable(),
      timestamp: new Date().toISOString()
    };

    if (!adapter.isAvailable()) {
      return {
        success: false,
        message: 'OneSignal plugin not available - may not be installed properly',
        details
      };
    }

    // Test basic plugin access
    const plugin = adapter.getPlugin();
    details.pluginStructure = {
      hasUser: !!plugin.User,
      hasNotifications: !!plugin.Notifications,
      userMethods: plugin.User ? Object.getOwnPropertyNames(plugin.User).slice(0, 10) : [],
      cordovaAvailable: !!(window.cordova && window.cordova.exec)
    };

    // Try to get device state with very short timeout
    
    try {
      const quickTimeout = new Promise<any>((_, reject) => {
        setTimeout(() => reject(new Error('Quick timeout')), 2000);
      });
      
      const deviceStatePromise = adapter.getDeviceState();
      const deviceState = await Promise.race([deviceStatePromise, quickTimeout]);
      
      details.deviceState = deviceState;
      details.responsive = true;
      
      
      
      // Check if user has proper External ID
      const { data: { user } } = await supabase.auth.getUser();
      if (user && deviceState) {
        details.externalIdCheck = {
          expected: user.id,
          actual: deviceState.externalUserId,
          matches: deviceState.externalUserId === user.id
        };
        
        if (deviceState.externalUserId !== user.id) {
          
          
          try {
            await adapter.login(user.id);
            
            details.externalIdFixed = true;
          } catch (fixError) {
            
            details.externalIdFixed = false;
            details.fixError = fixError;
          }
        }
      }
      
      return {
        success: true,
        message: 'OneSignal is working correctly',
        details
      };
      
    } catch (timeoutError) {
      
      details.responsive = false;
      details.timeoutError = timeoutError.message;
      
      // Try to reinitialize OneSignal
      
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const result = await initializeOneSignalWithProperExternalId(user.id);
          details.reinitResult = result;
          
          if (result.success) {
            return {
              success: true,
              message: 'OneSignal was unresponsive but has been reinitialized successfully',
              details
            };
          }
        }
      } catch (reinitError) {
        
        details.reinitError = reinitError;
      }
      
      return {
        success: false,
        message: 'OneSignal is unresponsive and could not be fixed',
        details
      };
    }

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown diagnostic error',
      details: { error }
    };
  }
}; 

/**
 * Global debug utility for OneSignal troubleshooting
 * Available in browser console as: window.debugOneSignal.testSDK5Methods()
 */
export const debugOneSignalSDK5Methods = async (): Promise<{
  success: boolean;
  message: string;
  details: any;
}> => {
  try {
    const adapter = getOneSignalAdapter();
    
    if (!adapter.isAvailable()) {
      return { success: false, message: 'OneSignal adapter not available', details: {} };
    }

    const plugin = adapter.getPlugin();
    const availableMethods = adapter.detectAvailableMethods();
    
    
    
    const testResults: any = {
      sdkVersion: availableMethods.sdkVersion,
      availableMethods,
      testResults: {}
    };

    // Helper function to add timeout to promises
    const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, methodName: string): Promise<T> => {
      return Promise.race([
        promise,
        new Promise<T>((_, reject) => 
          setTimeout(() => reject(new Error(`${methodName} timed out after ${timeoutMs}ms`)), timeoutMs)
        )
      ]);
    };

    // Test User.login() if available
    if (plugin.User && typeof plugin.User.login === 'function') {
      try {
        
        // Use a test external ID to avoid affecting real users
        await withTimeout(plugin.User.login('test-external-id-debug'), 5000, 'User.login()');
        testResults.testResults.userLogin = { success: true, method: 'User.login()' };
        
      } catch (loginError) {
        testResults.testResults.userLogin = { success: false, error: loginError, method: 'User.login()' };
        
      }
    } else {
      testResults.testResults.userLogin = { success: false, error: 'Method not available', method: 'User.login()' };
    }

    // Test Cordova login method
    if (window.cordova && window.cordova.exec) {
      try {
        
        const cordovaLoginPromise = new Promise((resolve, reject) => {
          window.cordova.exec(
            () => {
              testResults.testResults.cordovaLogin = { success: true, method: 'cordova.exec login' };
              
              resolve(undefined);
            },
            (error: any) => {
              testResults.testResults.cordovaLogin = { success: false, error, method: 'cordova.exec login' };
              
              reject(error);
            },
            'OneSignalPush', 
            'login',
            ['test-external-id-debug']
          );
        });
        
        await withTimeout(cordovaLoginPromise, 5000, 'Cordova login');
      } catch (cordovaLoginError) {
        if (!testResults.testResults.cordovaLogin) {
          testResults.testResults.cordovaLogin = { 
            success: false, 
            error: cordovaLoginError, 
            method: 'cordova.exec login' 
          };
        }
        
      }
    }

    // Test Cordova setExternalUserId method
    if (window.cordova && window.cordova.exec) {
      try {
        
        const cordovaSetExternalPromise = new Promise((resolve, reject) => {
          window.cordova.exec(
            () => {
              testResults.testResults.cordovaSetExternal = { success: true, method: 'cordova.exec setExternalUserId' };
              
              resolve(undefined);
            },
            (error: any) => {
              testResults.testResults.cordovaSetExternal = { success: false, error, method: 'cordova.exec setExternalUserId' };
              
              reject(error);
            },
            'OneSignalPush', 
            'setExternalUserId',
            ['test-external-id-debug']
          );
        });
        
        await withTimeout(cordovaSetExternalPromise, 5000, 'Cordova setExternalUserId');
      } catch (cordovaSetExternalError) {
        if (!testResults.testResults.cordovaSetExternal) {
          testResults.testResults.cordovaSetExternal = { 
            success: false, 
            error: cordovaSetExternalError, 
            method: 'cordova.exec setExternalUserId' 
          };
        }
        
      }
    }

    

    return {
      success: true,
      message: 'OneSignal SDK 5.x method testing completed',
      details: testResults
    };

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error during SDK testing',
      details: { error }
    };
  }
};

// Make debug utilities available globally
declare global {
  interface Window {
    debugOneSignal: {
      checkStatus: () => Promise<any>;
      quickFix: () => Promise<any>;
      reinitialize: () => Promise<any>;
      testSDK5Methods: () => Promise<any>;
      detectMethods: () => any;
    };
  }
}

if (typeof window !== 'undefined') {
  window.debugOneSignal = {
    checkStatus: quickOneSignalDiagnosticAndFix,
    quickFix: quickOneSignalDiagnosticAndFix,
    reinitialize: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          return await initializeOneSignalWithProperExternalId(user.id);
        } else {
          return { success: false, message: 'No user logged in' };
        }
      } catch (error) {
        return { success: false, message: 'Error reinitializing OneSignal', details: { error } };
      }
    },
    testSDK5Methods: debugOneSignalSDK5Methods,
    detectMethods: () => {
      const adapter = getOneSignalAdapter();
      return adapter.detectAvailableMethods();
    }
  };
}

// Export enhanced debugging utilities
export * from './enhanced-debug';

/**
 * Test function specifically for SDK 5.x external ID binding
 * This function tests all the new method names we've implemented
 */
export const testSDK5ExternalIdBinding = async (externalId: string): Promise<{
  success: boolean;
  message: string;
  details: any;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return { success: false, message: 'Not on native platform', details: {} };
    }

    
    

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      return { success: false, message: 'OneSignal adapter not available', details: {} };
    }

    // Get initial state
    const initialState = await adapter.getDeviceState();
    

    const testResults: any = {
      initialExternalId: initialState?.externalUserId,
      methodsTested: [],
      successfulMethod: null,
      finalExternalId: null
    };

    // Test methods in order of preference
    const methodsToTest = [
      {
        name: 'User.login',
        test: async () => {
          if (adapter.getPlugin().User?.login) {
            await adapter.getPlugin().User.login(externalId);
            return true;
          }
          return false;
        }
      },
      {
        name: 'User.addAlias',
        test: async () => {
          if (adapter.getPlugin().User?.addAlias) {
            await adapter.getPlugin().User.addAlias('external_id', externalId);
            return true;
          }
          return false;
        }
      },
      {
        name: 'Cordova login',
        test: async () => {
          return new Promise<boolean>((resolve) => {
            const timeoutId = setTimeout(() => resolve(false), 3000);
            window.cordova.exec(
              () => {
                clearTimeout(timeoutId);
                resolve(true);
              },
              () => {
                clearTimeout(timeoutId);
                resolve(false);
              },
              'OneSignalPush',
              'login',
              [externalId]
            );
          });
        }
      },
      {
        name: 'Cordova addAlias',
        test: async () => {
          return new Promise<boolean>((resolve) => {
            const timeoutId = setTimeout(() => resolve(false), 3000);
            window.cordova.exec(
              () => {
                clearTimeout(timeoutId);
                resolve(true);
              },
              () => {
                clearTimeout(timeoutId);
                resolve(false);
              },
              'OneSignalPush',
              'addAlias',
              ['external_id', externalId]
            );
          });
        }
      },
      {
        name: 'Cordova setExternalId',
        test: async () => {
          return new Promise<boolean>((resolve) => {
            const timeoutId = setTimeout(() => resolve(false), 3000);
            window.cordova.exec(
              () => {
                clearTimeout(timeoutId);
                resolve(true);
              },
              () => {
                clearTimeout(timeoutId);
                resolve(false);
              },
              'OneSignalPush',
              'setExternalId',
              [externalId]
            );
          });
        }
      },
      {
        name: 'Cordova User.addAlias',
        test: async () => {
          return new Promise<boolean>((resolve) => {
            const timeoutId = setTimeout(() => resolve(false), 3000);
            window.cordova.exec(
              () => {
                clearTimeout(timeoutId);
                resolve(true);
              },
              () => {
                clearTimeout(timeoutId);
                resolve(false);
              },
              'OneSignalPush',
              'User.addAlias',
              ['external_id', externalId]
            );
          });
        }
      }
    ];

    // Test each method
    for (const method of methodsToTest) {
      try {
        
        const methodResult = await method.test();
        
        testResults.methodsTested.push({
          name: method.name,
          success: methodResult,
          error: null
        });

        if (methodResult) {
          
          
          // Wait and verify
          await new Promise(resolve => setTimeout(resolve, 1000));
          const verificationState = await adapter.getDeviceState();
          
          if (verificationState?.externalUserId === externalId) {
            
            testResults.successfulMethod = method.name;
            testResults.finalExternalId = verificationState.externalUserId;
            break;
          } else {
            
          }
        } else {
          
        }
      } catch (error) {
        
        testResults.methodsTested.push({
          name: method.name,
          success: false,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }

    // Get final state
    const finalState = await adapter.getDeviceState();
    testResults.finalExternalId = finalState?.externalUserId;

    

    const success = testResults.successfulMethod !== null;
    return {
      success,
      message: success 
        ? `External ID set successfully using ${testResults.successfulMethod}` 
        : 'All methods failed to set external ID',
      details: testResults
    };

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown test error',
      details: { error }
    };
  }
};

/**
 * SOLUTION: Prevent Anonymous User Creation with Immediate External ID Binding
 * This function follows OneSignal best practices to avoid creating multiple users
 */
export const initializeOneSignalWithImmediateExternalId = async (supabaseUserId: string): Promise<{
  success: boolean;
  permissionGranted?: boolean;
  oneSignalUserId?: string;
  message: string;
  wasNewUser?: boolean;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return { success: false, message: 'Not on native platform' };
    }

    

    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    if (!oneSignalAppId) {
      
      return { success: false, message: 'OneSignal App ID not configured' };
    }

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { success: false, message: 'OneSignal plugin not available' };
    }

    // Step 1: Check if we already have the correct user bound
    let existingCorrectUser = false;
    try {
      const deviceState = await adapter.getDeviceState();
      if (deviceState?.externalUserId === supabaseUserId) {
        
        return {
          success: true,
          permissionGranted: deviceState.hasNotificationPermission || false,
          oneSignalUserId: deviceState.userId,
          message: 'User already properly bound',
          wasNewUser: false
        };
      } else if (deviceState?.externalUserId && deviceState.externalUserId !== supabaseUserId) {
        
        await adapter.logout();
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    } catch (checkError) {
      
    }

    // Step 2: CRITICAL - Set External ID BEFORE any OneSignal initialization
    // This prevents anonymous user creation entirely
    
    
    let externalIdSetSuccess = false;
    let externalIdMethod = '';

    // Try the most compatible methods first
    const externalIdMethods = [
      {
        name: 'Cordova login (pre-init)',
        execute: async () => {
          if (!window.cordova?.exec) return false;
          return new Promise<boolean>((resolve) => {
            const timeoutId = setTimeout(() => resolve(false), 3000);
            window.cordova.exec(
              () => {
                clearTimeout(timeoutId);
                resolve(true);
              },
              () => {
                clearTimeout(timeoutId);
                resolve(false);
              },
              'OneSignalPush',
              'login',
              [supabaseUserId]
            );
          });
        }
      },
      {
        name: 'User.login (pre-init)',
        execute: async () => {
          if (!adapter.getPlugin().User?.login) return false;
          try {
            await adapter.getPlugin().User.login(supabaseUserId);
            return true;
          } catch {
            return false;
          }
        }
      }
    ];

    // Try each external ID method
    for (const method of externalIdMethods) {
      try {
        
        const result = await method.execute();
        if (result) {
          
          externalIdSetSuccess = true;
          externalIdMethod = method.name;
          break;
        }
      } catch (error) {
        
      }
    }

    // Step 3: Initialize OneSignal SDK (should now bind to existing user with External ID)
    
    try {
      await adapter.setAppId(oneSignalAppId);
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (initError) {
      
      return { success: false, message: 'OneSignal initialization failed' };
    }

    // Step 4: Verify External ID was set correctly and retry if needed
    let finalState;
    try {
      finalState = await adapter.getDeviceState();
      
      if (finalState?.externalUserId !== supabaseUserId) {
        
        
        // Post-initialization external ID binding
        const postInitMethods = [
          {
            name: 'adapter.login',
            execute: async () => {
              await adapter.login(supabaseUserId);
              return true;
            }
          },
          {
            name: 'User.login (post-init)',
            execute: async () => {
              if (!adapter.getPlugin().User?.login) return false;
              await adapter.getPlugin().User.login(supabaseUserId);
              return true;
            }
          },
          {
            name: 'Cordova login (post-init)',
            execute: async () => {
              if (!window.cordova?.exec) return false;
              return new Promise<boolean>((resolve) => {
                const timeoutId = setTimeout(() => resolve(false), 3000);
                window.cordova.exec(
                  () => {
                    clearTimeout(timeoutId);
                    resolve(true);
                  },
                  () => {
                    clearTimeout(timeoutId);
                    resolve(false);
                  },
                  'OneSignalPush',
                  'login',
                  [supabaseUserId]
                );
              });
            }
          }
        ];

        for (const method of postInitMethods) {
          try {
            
            await method.execute();
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            finalState = await adapter.getDeviceState();
            if (finalState?.externalUserId === supabaseUserId) {
              
              externalIdMethod = method.name + ' (post-init)';
              break;
            }
          } catch (error) {
            
          }
        }
      }
    } catch (verifyError) {
      
    }

    // Step 5: Save to database
    try {
      if (finalState?.userId) {
        await saveOneSignalUserId(finalState.userId);
        
      }

      const updateData: any = {
        notification_enabled: finalState?.hasNotificationPermission || false,
        notification_permission_asked: true,
        updated_at: new Date().toISOString()
      };

      if (finalState?.userId) {
        updateData.onesignal_user_id = finalState.userId;
      }

      const { error: updateError } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', supabaseUserId);

      if (updateError) {
        
      }
    } catch (dbError) {
      
    }

    const success = finalState?.externalUserId === supabaseUserId;
    
    

    return {
      success,
      permissionGranted: finalState?.hasNotificationPermission || false,
      oneSignalUserId: finalState?.userId,
      message: success 
        ? `External ID set successfully using ${externalIdMethod}. User properly bound to prevent duplicates.`
        : 'Failed to set External ID - this may create duplicate users',
      wasNewUser: !existingCorrectUser
    };

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error during initialization',
      wasNewUser: false
    };
  }
};

/**
 * SOLUTION: Check if user already has OneSignal ID in database and reuse it
 * This prevents creating multiple OneSignal users for the same Supabase user
 */
export const initializeOneSignalWithExistingCheck = async (supabaseUserId: string): Promise<{
  success: boolean;
  permissionGranted?: boolean;
  oneSignalUserId?: string;
  message: string;
  wasExistingUser?: boolean;
  reusingExistingId?: boolean;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      
      return { success: false, message: 'Not on native platform' };
    }

    

    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    if (!oneSignalAppId) {
      
      return { success: false, message: 'OneSignal App ID not configured' };
    }

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { success: false, message: 'OneSignal plugin not available' };
    }

    // Step 1: Check if user already has OneSignal ID in database
    let existingOneSignalId: string | null = null;
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('onesignal_user_id')
        .eq('id', supabaseUserId)
        .single();

      existingOneSignalId = userData?.onesignal_user_id || null;
      
      if (existingOneSignalId) {
        
      } else {
        
      }
    } catch (dbError) {
      
    }

    // Step 2: Initialize OneSignal SDK
    try {
      await adapter.setAppId(oneSignalAppId);
      
      
      // Give OneSignal time to initialize
      await new Promise(resolve => setTimeout(resolve, 1500));
    } catch (initError) {
      
      return { success: false, message: 'Failed to initialize OneSignal SDK' };
    }

    // Step 3: Handle existing OneSignal ID case
    if (existingOneSignalId) {
      
      
      try {
        // Check current device state first
        const currentDeviceState = await adapter.getDeviceState();
        

        // If we already have the correct External ID set, we're good
        if (currentDeviceState?.externalUserId === supabaseUserId) {
          
          
          return {
            success: true,
            permissionGranted: currentDeviceState?.hasNotificationPermission || false,
            oneSignalUserId: currentDeviceState?.userId,
            message: 'Already bound to existing OneSignal user',
            wasExistingUser: true,
            reusingExistingId: true
          };
        }

        // If different user is bound, logout first
        if (currentDeviceState?.externalUserId && currentDeviceState.externalUserId !== supabaseUserId) {
          
          await adapter.logout();
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        // Set External ID to bind to our Supabase user
        
        await adapter.login(supabaseUserId);
        
        // Wait for binding to complete
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Verify the binding worked
        const updatedDeviceState = await adapter.getDeviceState();
        if (updatedDeviceState?.externalUserId === supabaseUserId) {
          
          
          // Update database with latest info if needed
          const updateData: any = {
            notification_enabled: updatedDeviceState?.hasNotificationPermission || false,
            notification_permission_asked: true,
            updated_at: new Date().toISOString()
          };

          // Only update OneSignal ID if it changed (shouldn't happen but safety check)
          if (updatedDeviceState?.userId && updatedDeviceState.userId !== existingOneSignalId) {
            updateData.onesignal_user_id = updatedDeviceState.userId;
            
          }

          await supabase
            .from('users')
            .update(updateData)
            .eq('id', supabaseUserId);

          return {
            success: true,
            permissionGranted: updatedDeviceState?.hasNotificationPermission || false,
            oneSignalUserId: updatedDeviceState?.userId,
            message: 'Successfully reused existing OneSignal user',
            wasExistingUser: true,
            reusingExistingId: true
          };
        } else {
          
        }
      } catch (reuseError) {
        
        
      }
    }

    // Step 4: Create new OneSignal user (either no existing ID or reuse failed)
    
    
    try {
      // Set External ID immediately to prevent anonymous user creation
      await adapter.login(supabaseUserId);
      
      
      // Wait for OneSignal to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Get the new device state
      const finalDeviceState = await adapter.getDeviceState();
      const newOneSignalUserId = finalDeviceState?.userId;
      
      if (newOneSignalUserId) {
        
        
        // Save new OneSignal ID to database
        const updateData: any = {
          onesignal_user_id: newOneSignalUserId,
          notification_enabled: finalDeviceState?.hasNotificationPermission || false,
          notification_permission_asked: true,
          updated_at: new Date().toISOString()
        };

        await supabase
          .from('users')
          .update(updateData)
          .eq('id', supabaseUserId);

        

        return {
          success: true,
          permissionGranted: finalDeviceState?.hasNotificationPermission || false,
          oneSignalUserId: newOneSignalUserId,
          message: 'New OneSignal user created and saved',
          wasExistingUser: false,
          reusingExistingId: false
        };
      } else {
        
        return {
          success: false,
          message: 'Failed to create OneSignal user'
        };
      }
    } catch (createError) {
      
      return {
        success: false,
        message: 'Failed to create new OneSignal user'
      };
    }

  } catch (error) {
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown initialization error'
    };
  }
};

/**
 * Utility function to check current OneSignal user status
 * Useful for debugging and testing the fix
 */
export const getOneSignalUserStatus = async (supabaseUserId: string): Promise<{
  hasStoredId: boolean;
  storedId?: string;
  currentDeviceUserId?: string;
  currentExternalId?: string;
  isProperlyBound: boolean;
  message: string;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return {
        hasStoredId: false,
        isProperlyBound: false,
        message: 'Not on native platform'
      };
    }

    // Check database for stored OneSignal ID
    let storedId: string | undefined;
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('onesignal_user_id')
        .eq('id', supabaseUserId)
        .single();

      storedId = userData?.onesignal_user_id || undefined;
    } catch (dbError) {
      
    }

    // Check current OneSignal device state
    let currentDeviceUserId: string | undefined;
    let currentExternalId: string | undefined;
    try {
      const adapter = getOneSignalAdapter();
      if (adapter.isAvailable()) {
        const deviceState = await adapter.getDeviceState();
        currentDeviceUserId = deviceState?.userId;
        currentExternalId = deviceState?.externalUserId;
      }
    } catch (deviceError) {
      
    }

    const hasStoredId = !!storedId;
    const isProperlyBound = currentExternalId === supabaseUserId;

    let message = '';
    if (!hasStoredId && !currentDeviceUserId) {
      message = 'No OneSignal user found (neither stored nor current)';
    } else if (hasStoredId && isProperlyBound) {
      message = 'OneSignal user properly bound and stored';
    } else if (hasStoredId && !isProperlyBound) {
      message = 'OneSignal ID stored but not properly bound to current user';
    } else if (!hasStoredId && currentDeviceUserId) {
      message = 'OneSignal user exists but not stored in database';
    } else {
      message = 'OneSignal user status unclear';
    }

    return {
      hasStoredId,
      storedId,
      currentDeviceUserId,
      currentExternalId,
      isProperlyBound,
      message
    };

  } catch (error) {
    return {
      hasStoredId: false,
      isProperlyBound: false,
      message: error instanceof Error ? error.message : 'Error checking status'
    };
  }
};